/**
 * Life4Earth V2 — Role Digest Data Hook
 *
 * Composes Gantt tasks, RACI assignments, issue flags, and acknowledgments
 * into the RACI-scoped Digest shape. Client-side composition of existing
 * API data — no new backend endpoint required for Phase 1.
 *
 * Epic: DEV-712, Task: DEV-713
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import {
  fetchGanttTasksByProject,
  patchGanttTaskProgress,
  type GanttTaskResponse,
} from "../../api/ganttTasks";
import {
  fetchTaskRoleAssignments,
  buildTaskRoleAssignmentMap,
  type TaskRoleAssignmentEnriched,
  type ResponsibilityType,
} from "../../api/ganttTaskRoleAssignments";
import {
  fetchIssueFlags,
  createIssueFlag,
  type IssueFlagResponse,
  type IssueSeverity,
} from "../../api/issueFlags";
import {
  fetchDigestAcknowledgments,
  acknowledgeDigestItem,
  type DigestAcknowledgmentResponse,
} from "../../api/digestAcknowledgments";
import {
  fetchTaskConsultations,
  createTaskConsultation,
  type TaskConsultationResponse,
} from "../../api/taskConsultations";
import {
  fetchForKey,
  invalidate,
  CACHE_UPDATE_EVENT,
} from "../../api/queryCache";

// ── Types ─────────────────────────────────────────────────────

export type UrgencyTier =
  | "overdue_blocked"
  | "overdue"
  | "imminent"
  | "upcoming"
  | "informational";

export type RaciType = "R" | "A" | "C" | "I";

export interface DigestBlocker {
  taskName: string;
  ownerName: string;
  percentComplete: number;
}

export interface DigestIssue {
  id: string;
  description: string;
  severity: IssueSeverity;
  flaggedBy: string;
  flaggedAt: string;
}

export interface DigestTask {
  /** Gantt task ID */
  id: string;
  taskName: string;
  wbsPath: string;
  startDate: string | null;
  endDate: string | null;
  percentComplete: number;
  isMilestone: boolean;
  isSummary: boolean;
  raciType: RaciType;
  urgencyTier: UrgencyTier;
  blockers: DigestBlocker[];
  issues: DigestIssue[];
  acknowledged: boolean;
  /** I-mode: optional rationale captured at ack time. DEV-736. */
  acknowledgmentNote: string | null;
  /** For A/C items: who is the R person? */
  assignedTo: {
    name: string;
    roleSlotGuid: string;
    lastUpdated: string | null;
    daysSinceUpdate: number | null;
  } | null;
  /** C-mode: has consultation been requested? */
  consultationRequested: boolean;
  /** C-mode: has the user provided input? */
  inputProvided: boolean;
  /** C-mode: the user's most recent consultation response text. DEV-737. */
  consultationResponseText: string | null;
  /** I-mode: what changed? */
  changeDescription: string | null;
}

export interface DigestSummary {
  totalR: number;
  totalA: number;
  totalC: number;
  totalI: number;
  overdue: number;
  blocked: number;
  unacknowledged: number;
}

export interface DigestDateRange {
  start: string;     // YYYY-MM-DD
  end: string;       // YYYY-MM-DD
  label: string;
}

export interface UseRoleDigestResult {
  rTasks: DigestTask[];
  aTasks: DigestTask[];
  cTasks: DigestTask[];
  iTasks: DigestTask[];
  summary: DigestSummary;
  loading: boolean;
  error: string | null;
  /** Update percent complete on a task (optimistic) */
  updatePercent: (taskId: string, value: number) => Promise<void>;
  /** Mark a task as 100% complete (optimistic) */
  markComplete: (taskId: string) => Promise<void>;
  /** Flag an issue on a task */
  flagIssue: (taskId: string, description: string, severity: IssueSeverity) => Promise<void>;
  /**
   * Acknowledge an I-type item. Optional `note` captures governance rationale.
   * On a subsequent acknowledge with a new note, the existing record's note
   * is updated (note-append flow). DEV-736.
   */
  acknowledge: (taskId: string, note?: string) => Promise<void>;
  /**
   * Record a Consulted-party response on a task. Stored as a governance
   * receipt on the `task_consultations` table — not a comment. Empty
   * responses are rejected. DEV-737.
   */
  respond: (taskId: string, responseText: string) => Promise<void>;
  /** Force refetch */
  refetch: () => void;
}

// ── Helpers ───────────────────────────────────────────────────

const TODAY = () => {
  const d = new Date();
  return d.toISOString().slice(0, 10);
};

function daysBetween(dateStr: string | null, reference: string): number | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const r = new Date(reference);
  return Math.ceil((d.getTime() - r.getTime()) / (1000 * 60 * 60 * 24));
}

function raciChar(type: string): RaciType {
  switch (type.toLowerCase()) {
    case "responsible": return "R";
    case "accountable": return "A";
    case "consulted": return "C";
    case "informed": return "I";
    default: return "R";
  }
}

function computeUrgencyTier(
  task: GanttTaskResponse,
  isBlocked: boolean,
  raciType: RaciType
): UrgencyTier {
  if (raciType === "I") return "informational";
  if (task.percentComplete >= 100) return "upcoming"; // complete tasks are no longer urgent
  const today = TODAY();
  const daysToEnd = daysBetween(task.endDate, today);
  if (daysToEnd !== null && daysToEnd < 0) {
    return isBlocked ? "overdue_blocked" : "overdue";
  }
  if (daysToEnd !== null && daysToEnd <= 2) {
    return "imminent";
  }
  return "upcoming";
}

/** Sort tasks by urgency tier then by due date asc. */
const TIER_ORDER: Record<UrgencyTier, number> = {
  overdue_blocked: 0,
  overdue: 1,
  imminent: 2,
  upcoming: 3,
  informational: 4,
};

function sortByUrgency(a: DigestTask, b: DigestTask): number {
  const tierDiff = TIER_ORDER[a.urgencyTier] - TIER_ORDER[b.urgencyTier];
  if (tierDiff !== 0) return tierDiff;
  // Within same tier: earlier due date first
  const aDate = a.endDate ? new Date(a.endDate).getTime() : Infinity;
  const bDate = b.endDate ? new Date(b.endDate).getTime() : Infinity;
  return aDate - bDate;
}

/** Find the WBS path label for a task (walk up to its summary parent). */
function resolveWbsPath(
  task: GanttTaskResponse,
  taskMap: Map<string, GanttTaskResponse>
): string {
  // Walk up indent levels to find the nearest summary task
  const sorted = Array.from(taskMap.values()).sort(
    (a, b) => a.sortOrder - b.sortOrder
  );
  const taskIdx = sorted.findIndex(
    (t) => t.id.toUpperCase() === task.id.toUpperCase()
  );
  if (taskIdx < 0) return "";
  // Walk backwards to find a summary task with lower indent
  for (let i = taskIdx - 1; i >= 0; i--) {
    const candidate = sorted[i];
    if (candidate.isSummary && candidate.indentLevel < task.indentLevel) {
      return candidate.taskName;
    }
  }
  return "";
}

// ── Hook ──────────────────────────────────────────────────────

export function useRoleDigestData(
  projectGuid: string | null,
  roleSlotGuids: string[],
  currentUserGuid: string,
  dateRange: { start: string; end: string } | null
): UseRoleDigestResult {
  const [ganttTasks, setGanttTasks] = useState<GanttTaskResponse[]>([]);
  const [assignments, setAssignments] = useState<TaskRoleAssignmentEnriched[]>([]);
  const [issueFlags, setIssueFlags] = useState<IssueFlagResponse[]>([]);
  const [acks, setAcks] = useState<DigestAcknowledgmentResponse[]>([]);
  const [consultations, setConsultations] = useState<TaskConsultationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Normalize role guids for case-insensitive comparison
  const roleGuidsUpper = useMemo(
    () => new Set(roleSlotGuids.map((g) => g.toUpperCase())),
    [roleSlotGuids]
  );

  const cacheKey = projectGuid
    ? `roleDigest:${projectGuid}:${roleSlotGuids.join(",")}`
    : null;

  const fetchAll = useCallback(async () => {
    void cacheKey; // recompute key (derived from projectGuid + roleSlotGuids, already deps)
    if (!projectGuid || roleSlotGuids.length === 0) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Core data (Gantt tasks + RACI assignments) — these must succeed
      const [tasks, assigns] = await Promise.all([
        fetchForKey(`ganttTasks:${projectGuid}`, () =>
          fetchGanttTasksByProject(projectGuid)
        ),
        fetchForKey(`taskRoleAssignments:${projectGuid}`, () =>
          fetchTaskRoleAssignments(projectGuid)
        ),
      ]);
      setGanttTasks(tasks ?? []);
      setAssignments(assigns ?? []);

      // Supplemental data (flags + acks + consultations) — fail gracefully
      const [flags, acknowledgments, consults] = await Promise.all([
        fetchIssueFlags(projectGuid).catch(() => [] as IssueFlagResponse[]),
        fetchDigestAcknowledgments(projectGuid, currentUserGuid).catch(
          () => [] as DigestAcknowledgmentResponse[]
        ),
        fetchTaskConsultations(projectGuid, currentUserGuid).catch(
          () => [] as TaskConsultationResponse[]
        ),
      ]);
      setIssueFlags(flags ?? []);
      setAcks(acknowledgments ?? []);
      setConsultations(consults ?? []);
    } catch (err: any) {
      setError(err?.message ?? "Failed to load digest data");
    } finally {
      setLoading(false);
    }
  }, [projectGuid, roleSlotGuids, currentUserGuid, cacheKey]);

  // Initial fetch + subscribe to cache updates
  useEffect(() => {
    fetchAll();
    window.addEventListener(CACHE_UPDATE_EVENT, fetchAll);
    return () => window.removeEventListener(CACHE_UPDATE_EVENT, fetchAll);
  }, [fetchAll]);

  // ── Build digest tasks ─────────────────────────────────────

  const digestData = useMemo(() => {
    if (loading || !ganttTasks.length) {
      return {
        rTasks: [] as DigestTask[],
        aTasks: [] as DigestTask[],
        cTasks: [] as DigestTask[],
        iTasks: [] as DigestTask[],
        summary: { totalR: 0, totalA: 0, totalC: 0, totalI: 0, overdue: 0, blocked: 0, unacknowledged: 0 },
      };
    }

    // Build lookup maps
    const taskMap = new Map<string, GanttTaskResponse>();
    const taskByTaskId = new Map<number, GanttTaskResponse>();
    for (const t of ganttTasks) {
      taskMap.set(t.id.toUpperCase(), t);
      if (t.taskId != null) taskByTaskId.set(t.taskId, t);
    }

    const assignmentMap = buildTaskRoleAssignmentMap(assignments);

    // Build issue flag map: ganttTaskId → IssueFlagResponse[]
    const issueFlagMap = new Map<string, IssueFlagResponse[]>();
    for (const f of issueFlags) {
      if (!f.ganttTaskId) continue;
      const key = f.ganttTaskId.toUpperCase();
      const arr = issueFlagMap.get(key) ?? [];
      arr.push(f);
      issueFlagMap.set(key, arr);
    }

    // Build ack set + note lookup (DEV-736)
    const ackSet = new Set<string>();
    const ackNoteMap = new Map<string, string | null>();
    for (const a of acks) {
      const key = `${a.ganttTaskId.toUpperCase()}:${a.roleSlotGuid.toUpperCase()}`;
      ackSet.add(key);
      ackNoteMap.set(key, a.note ?? null);
    }

    // Consultation lookup (DEV-737) — keyed by (task, role_slot) → most-recent
    // response by the current user. Consultations are time-ordered; we want
    // the latest so the digest always reflects the freshest input.
    const consultMap = new Map<string, TaskConsultationResponse>();
    for (const c of consultations) {
      const key = `${c.ganttTaskId.toUpperCase()}:${c.roleSlotGuid.toUpperCase()}`;
      const prev = consultMap.get(key);
      if (!prev || new Date(c.respondedAt) > new Date(prev.respondedAt)) {
        consultMap.set(key, c);
      }
    }

    // Filter assignments to user's role slots
    const myAssignments = assignments.filter((a) =>
      roleGuidsUpper.has(a.roleSlotGuid.toUpperCase())
    );

    const today = TODAY();
    const digestTasks: DigestTask[] = [];

    for (const assign of myAssignments) {
      const taskKey = assign.ganttTaskId.toUpperCase();
      const task = taskMap.get(taskKey);
      if (!task || task.isSummary) continue; // skip summary rows

      const raciType = raciChar(assign.responsibilityType);

      // Date range filter (if provided)
      if (dateRange) {
        const taskEnd = task.endDate;
        const taskStart = task.startDate;
        // Include overdue tasks regardless of range
        const isOverdue = taskEnd && new Date(taskEnd) < new Date(today);
        if (!isOverdue) {
          // Exclude tasks entirely outside the range
          if (taskStart && new Date(taskStart) > new Date(dateRange.end)) continue;
          if (taskEnd && new Date(taskEnd) < new Date(dateRange.start)) continue;
        }
      }

      // Predecessor blocked detection
      const predecessorIds = (task.predecessors ?? "")
        .split(",")
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !isNaN(n));
      const blockers: DigestBlocker[] = [];
      for (const predId of predecessorIds) {
        const predTask = taskByTaskId.get(predId);
        if (predTask && predTask.percentComplete < 100) {
          // Find who is Responsible for the predecessor
          const predAssigns = assignmentMap.get(predTask.id.toUpperCase()) ?? [];
          const rAssign = predAssigns.find(
            (a) => a.responsibilityType === "responsible"
          );
          blockers.push({
            taskName: predTask.taskName,
            ownerName: rAssign?.roleLabel ?? "Unassigned",
            percentComplete: predTask.percentComplete,
          });
        }
      }

      const isBlocked = blockers.length > 0;
      const urgencyTier = computeUrgencyTier(task, isBlocked, raciType);

      // Resolve who the R person is (for A and C items)
      let assignedTo: DigestTask["assignedTo"] = null;
      if (raciType === "A" || raciType === "C") {
        const taskAssigns = assignmentMap.get(taskKey) ?? [];
        const rAssign = taskAssigns.find(
          (a) => a.responsibilityType === "responsible"
        );
        if (rAssign) {
          const daysSince = task.updatedAt
            ? Math.max(
                0,
                Math.floor(
                  (new Date(today).getTime() -
                    new Date(task.updatedAt).getTime()) /
                    (1000 * 60 * 60 * 24)
                )
              )
            : null;
          assignedTo = {
            name: rAssign.roleLabel,
            roleSlotGuid: rAssign.roleSlotGuid,
            lastUpdated: task.updatedAt,
            daysSinceUpdate: daysSince,
          };
        }
      }

      // Issue flags for this task (open only)
      const taskFlags = (issueFlagMap.get(taskKey) ?? []).filter(
        (f) => f.status === "open"
      );
      const digestIssues: DigestIssue[] = taskFlags.map((f) => ({
        id: f.id,
        description: f.description,
        severity: f.severity,
        flaggedBy: f.reportedByUserGuid, // TODO: resolve to display name
        flaggedAt: new Date(f.createdAt).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
      }));

      // Acknowledgment check
      const ackKey = `${taskKey}:${assign.roleSlotGuid.toUpperCase()}`;
      const acknowledged = ackSet.has(ackKey);
      const acknowledgmentNote = acknowledged ? (ackNoteMap.get(ackKey) ?? null) : null;

      const wbsPath = resolveWbsPath(task, taskMap);

      digestTasks.push({
        id: task.id,
        taskName: task.taskName,
        wbsPath,
        startDate: task.startDate,
        endDate: task.endDate,
        percentComplete: task.percentComplete,
        isMilestone: task.isMilestone,
        isSummary: false,
        raciType,
        urgencyTier,
        blockers,
        issues: digestIssues,
        acknowledged,
        acknowledgmentNote,
        assignedTo,
        consultationRequested: false,  // Phase 2: Accountable-initiated requests (deferred)
        inputProvided: consultMap.has(ackKey),           // DEV-737
        consultationResponseText: consultMap.get(ackKey)?.responseText ?? null,
        changeDescription: null,       // TODO: derive from recent change log
      });
    }

    // Split by RACI type and sort each by urgency
    const rTasks = digestTasks.filter((t) => t.raciType === "R").sort(sortByUrgency);
    const aTasks = digestTasks.filter((t) => t.raciType === "A").sort(sortByUrgency);
    const cTasks = digestTasks.filter((t) => t.raciType === "C").sort(sortByUrgency);
    const iTasks = digestTasks.filter((t) => t.raciType === "I").sort(sortByUrgency);

    const overdueTasks = digestTasks.filter(
      (t) => t.urgencyTier === "overdue" || t.urgencyTier === "overdue_blocked"
    );
    const blockedTasks = digestTasks.filter(
      (t) => t.urgencyTier === "overdue_blocked"
    );
    const unacked = iTasks.filter((t) => !t.acknowledged);

    return {
      rTasks,
      aTasks,
      cTasks,
      iTasks,
      summary: {
        totalR: rTasks.length,
        totalA: aTasks.length,
        totalC: cTasks.length,
        totalI: iTasks.length,
        overdue: overdueTasks.length,
        blocked: blockedTasks.length,
        unacknowledged: unacked.length,
      },
    };
  }, [
    loading,
    ganttTasks,
    assignments,
    issueFlags,
    acks,
    consultations,
    roleGuidsUpper,
    dateRange,
  ]);

  // ── Actions ─────────────────────────────────────────────────

  const updatePercent = useCallback(
    async (taskId: string, value: number) => {
      // Optimistic update
      setGanttTasks((prev) =>
        prev.map((t) =>
          t.id.toUpperCase() === taskId.toUpperCase()
            ? { ...t, percentComplete: value }
            : t
        )
      );
      try {
        await patchGanttTaskProgress(taskId, value);
        // DEV-842: Invalidate the cached ganttTasks list so any subsequent
        // CACHE_UPDATE_EVENT (SignalR push, sibling action, etc.) doesn't
        // return stale cached data and overwrite the optimistic update.
        // Without this, the "Done" button appeared to do nothing — the UI
        // snapped back to 0% as soon as any cache event fired, because
        // fetchAll → fetchForKey returned the pre-patch cached payload.
        if (projectGuid) {
          invalidate(`ganttTasks:${projectGuid}`);
        }
      } catch {
        // Revert on failure — refetch will restore correct state
        fetchAll();
      }
    },
    [fetchAll, projectGuid]
  );

  const markComplete = useCallback(
    async (taskId: string) => {
      await updatePercent(taskId, 100);
    },
    [updatePercent]
  );

  const flagIssue = useCallback(
    async (taskId: string, description: string, severity: IssueSeverity) => {
      if (!projectGuid) return;
      try {
        const flag = await createIssueFlag(projectGuid, {
          ganttTaskId: taskId,
          description,
          severity,
        });
        setIssueFlags((prev) => [...prev, flag]);
      } catch {
        // Swallow — user can retry
      }
    },
    [projectGuid]
  );

  const acknowledge = useCallback(
    async (taskId: string, note?: string) => {
      if (!projectGuid || roleSlotGuids.length === 0) return;
      const roleSlotGuid = roleSlotGuids[0]; // primary role
      const cleanNote = note && note.trim() ? note.trim() : null;
      // Optimistic
      setAcks((prev) => {
        const existing = prev.find(
          (a) => a.ganttTaskId === taskId && a.roleSlotGuid === roleSlotGuid
        );
        if (existing) {
          // Note-append: update in place if a new note is provided
          if (cleanNote && cleanNote !== existing.note) {
            return prev.map((a) =>
              a.ganttTaskId === taskId && a.roleSlotGuid === roleSlotGuid
                ? { ...a, note: cleanNote }
                : a,
            );
          }
          return prev;
        }
        return [
          ...prev,
          {
            userGuid: currentUserGuid,
            ganttTaskId: taskId,
            roleSlotGuid,
            acknowledgedAt: new Date().toISOString(),
            note: cleanNote,
          },
        ];
      });
      try {
        await acknowledgeDigestItem(projectGuid, {
          ganttTaskId: taskId,
          roleSlotGuid,
          note: cleanNote,
        });
      } catch {
        // Revert — only drop optimistic record if one wasn't already present
        setAcks((prev) =>
          prev.filter(
            (a) => !(a.ganttTaskId === taskId && a.roleSlotGuid === roleSlotGuid)
          )
        );
      }
    },
    [projectGuid, roleSlotGuids, currentUserGuid]
  );

  const respond = useCallback(
    async (taskId: string, responseText: string) => {
      if (!projectGuid || roleSlotGuids.length === 0) return;
      const clean = responseText.trim();
      if (!clean) return; // empty is rejected server-side; short-circuit
      const roleSlotGuid = roleSlotGuids[0];
      const optimistic: TaskConsultationResponse = {
        id: `optimistic-${Date.now()}`,
        ganttTaskId: taskId,
        consultedUserGuid: currentUserGuid,
        roleSlotGuid,
        responseText: clean,
        respondedAt: new Date().toISOString(),
      };
      setConsultations((prev) => [...prev, optimistic]);
      try {
        const created = await createTaskConsultation(projectGuid, {
          ganttTaskId: taskId,
          roleSlotGuid,
          responseText: clean,
        });
        // Replace optimistic with server record
        setConsultations((prev) =>
          prev.map((c) => (c.id === optimistic.id ? created : c)),
        );
      } catch {
        // Revert optimistic entry on failure
        setConsultations((prev) => prev.filter((c) => c.id !== optimistic.id));
      }
    },
    [projectGuid, roleSlotGuids, currentUserGuid],
  );

  return {
    ...digestData,
    loading,
    error,
    updatePercent,
    markComplete,
    flagIssue,
    acknowledge,
    respond,
    refetch: fetchAll,
  };
}
